import { useId } from 'react';

/**
 * Generates consistent accessibility IDs for related elements.
 * Useful for connecting labels, inputs, errors, and descriptions.
 *
 * @param prefix - Optional prefix for the ID
 * @returns Object with related IDs
 *
 * @example
 * ```tsx
 * const ids = useA11yId('email');
 * // ids.id = 'email-:r1:'
 * // ids.labelId = 'email-:r1:-label'
 * // ids.errorId = 'email-:r1:-error'
 * // etc.
 * ```
 */
export function useA11yId(prefix?: string) {
  const reactId = useId();
  const baseId = prefix ? `${prefix}-${reactId}` : reactId;

  return {
    /** Base ID for the primary element */
    id: baseId,
    /** ID for the label element */
    labelId: `${baseId}-label`,
    /** ID for the description/help text element */
    describedById: `${baseId}-description`,
    /** ID for the error message element */
    errorId: `${baseId}-error`,
    /** ID for a trigger element (e.g., accordion button) */
    triggerId: `${baseId}-trigger`,
    /** ID for content element (e.g., accordion panel) */
    contentId: `${baseId}-content`,
    /** ID for a listbox */
    listboxId: `${baseId}-listbox`,
    /** Generate an option ID */
    optionId: (value: string) => `${baseId}-option-${value}`,
  };
}
